import * as React from "react"
import { Link, router } from "@inertiajs/react"
import { Archive, GitBranchPlus, Lock, MailCheck, MoreHorizontal, Plus, RefreshCw } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { FollowupDateControl } from "@/components/inbox/FollowupDateControl"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { TaskList } from "@/components/tasks/TaskList"
import { TaskFormDialog } from "@/components/tasks/TaskFormDialog"
import { cn } from "@/lib/utils"
import { classificationLabel, formatInboxDate, smartleadInboxUrl, statusLabel } from "./helpers"
import type { SelectedReply } from "./types"

type ThreadPaneProps = {
  readonly reply: SelectedReply | null
  readonly clients?: readonly { readonly id: number; readonly display_name: string }[]
  readonly classifications?: readonly string[]
}

export function ThreadPane({ reply, clients = [], classifications = [] }: ThreadPaneProps) {
  const [wonDialogOpen, setWonDialogOpen] = React.useState(false)
  const [lostDialogOpen, setLostDialogOpen] = React.useState(false)
  const [lostReason, setLostReason] = React.useState("")
  const [tasksOpen, setTasksOpen] = React.useState(false)

  if (!reply) {
    return (
      <section className="flex min-h-[420px] items-center justify-center rounded-lg border border-hairline bg-surface p-6 text-center">
        <div>
          <h2>Select a reply</h2>
          <p className="mt-2 max-w-sm">Open a thread to review context, edit a draft, and preview the SmartLead payload.</p>
        </div>
      </section>
    )
  }

  const smartleadUrl = smartleadInboxUrl(reply)

  return (
    <section className="flex flex-col rounded-lg border border-hairline bg-page">
      <header className="border-b border-hairline px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-ink-display truncate">{reply.lead_name}</h3>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ink-muted">
              <span className="truncate">{reply.lead_email}</span>
              <Badge tone={reply.status === "sent" ? "signal" : "muted"} className="text-[11px]">
                {statusLabel(reply.status)}
              </Badge>
              {reply.client && <Badge tone="neutral" className="text-[11px]">{reply.client.display_name}</Badge>}
              {reply.company && <Badge tone="muted" className="text-[11px]">{reply.company}</Badge>}
              {reply.campaign_name && <Badge tone="muted" className="text-[11px]">{reply.campaign_name}</Badge>}
              {reply.op_lane && <Badge tone="muted" className="text-[11px]">Lane: {reply.op_lane}</Badge>}
            </div>
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-medium uppercase tracking-wide text-ink-muted">
            Type
          </span>
          <Select
            aria-label="Category"
            value={reply.classification}
            onChange={(event) =>
              router.post(
                `/replies/${reply.id}/category`,
                { category: event.target.value },
                { preserveScroll: true },
              )
            }
            className="h-8 w-auto min-w-36 text-xs"
          >
            {classifications.map((classification) => (
              <option key={classification} value={classification}>
                {classificationLabel(classification)}
              </option>
            ))}
          </Select>
          {reply.human_categorized_at && (
            <>
              <Badge tone="neutral" className="text-[11px]">
                <Lock className="h-3 w-3" />
                Manual
              </Badge>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  router.post(`/replies/${reply.id}/category`, { clear: true }, { preserveScroll: true })
                }
              >
                Clear
              </Button>
            </>
          )}

          {reply.op_stage && (
            <span
              className="inline-flex"
              title="Follow-up stage — auto-managed. This is what schedules the automatic follow-up cadence; it's usually set for you (on send and on won/lost)."
            >
              <Badge tone="muted" className="text-[11px]">
                Progress: {humanizeStage(reply.op_stage)}
              </Badge>
            </span>
          )}

          <CrmControl reply={reply} clients={clients} />

          {reply.status !== "archived" && reply.status !== "sent" && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => router.post(`/replies/${reply.id}/mark_responded`, {}, { preserveScroll: true })}
              title="I already replied to this outside the app (e.g. in SmartLead)"
            >
              <MailCheck className="h-4 w-4" />
              Mark responded
            </Button>
          )}

          {reply.status === "archived" ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => router.post(`/replies/${reply.id}/unarchive`, {}, { preserveScroll: true })}
            >
              <RefreshCw className="h-4 w-4" />
              Restore
            </Button>
          ) : (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => router.post(`/replies/${reply.id}/archive`, {}, { preserveScroll: true })}
            >
              <Archive className="h-4 w-4" />
              Archive
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="secondary" size="sm" aria-label="More actions">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {smartleadUrl && (
                <DropdownMenuItem asChild>
                  <a href={smartleadUrl} target="_blank" rel="noopener noreferrer" className="no-underline">
                    Open in SmartLead
                  </a>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onSelect={() => router.post(`/replies/${reply.id}/sync`, {}, { preserveScroll: true })}
              >
                Sync from SmartLead
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => router.post(`/replies/${reply.id}/snooze`, {}, { preserveScroll: true })}
              >
                Snooze 24h
              </DropdownMenuItem>
              {reply.subsequence_rule && (
                <DropdownMenuItem
                  onSelect={() => router.post(`/replies/${reply.id}/subsequence_push`, {}, { preserveScroll: true })}
                >
                  <GitBranchPlus className="h-4 w-4" />
                  Push subsequence
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setWonDialogOpen(true)}>
                Mark as won…
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setLostDialogOpen(true)}>
                Mark as lost…
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

        </div>

        <NextActionBlock
          key={reply.id}
          replyId={reply.id}
          note={reply.next_step_note}
          nextFollowupOn={reply.next_followup_on}
        />

        <Dialog open={wonDialogOpen} onOpenChange={setWonDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark as won?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-ink-muted">
              This also stops or completes sibling leads and may blocklist the company's domain.
            </p>
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setWonDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  router.post(
                    `/replies/bulk`,
                    { bulk_action: "won", ids: String(reply.id) },
                    { preserveScroll: true },
                  )
                  setWonDialogOpen(false)
                }}
              >
                Mark as won
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark as lost?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-ink-muted">
              This archives the thread and marks the deal lost. Add a reason (optional).
            </p>
            <Input
              aria-label="Reason"
              value={lostReason}
              placeholder="Reason (optional)"
              onChange={(event) => setLostReason(event.target.value)}
            />
            <DialogFooter>
              <Button type="button" variant="secondary" onClick={() => setLostDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => {
                  router.post(
                    `/replies/${reply.id}/op_stage`,
                    { op_stage: "lost", reason: lostReason || undefined },
                    { preserveScroll: true },
                  )
                  setLostDialogOpen(false)
                  setLostReason("")
                }}
              >
                Mark as lost
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>
      <div className="space-y-4 p-4">
        {reply.thread_messages.length > 0 ? (
          reply.thread_messages.map((message) => (
            <article
              key={message.id}
              className={cn(
                "max-w-[88%] rounded-lg border border-hairline p-3",
                message.direction === "outbound"
                  ? "ml-auto bg-accent-faded"
                  : "bg-surface",
              )}
            >
              <div className="mb-2 flex items-center justify-between gap-3 text-xs text-ink-muted">
                <span className="uppercase">{message.direction}</span>
                <time>{formatInboxDate(message.sent_at)}</time>
              </div>
              <div
                className="prose prose-sm max-w-none text-ink-body"
                dangerouslySetInnerHTML={{ __html: message.body_html || message.body_text }}
              />
            </article>
          ))
        ) : (
          <div className="rounded-lg border border-dashed border-hairline p-6 text-center">
            <p>No synced thread history yet.</p>
          </div>
        )}
      </div>
      <div className="border-t border-hairline p-4">
        <button
          type="button"
          className="flex w-full items-center justify-between gap-3 text-left"
          onClick={() => setTasksOpen((open) => !open)}
          aria-expanded={tasksOpen}
        >
          <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
            Other tasks ({reply.tasks?.length ?? 0})
          </span>
          <span aria-hidden="true" className="text-ink-muted">
            {tasksOpen ? "▾" : "▸"}
          </span>
        </button>
        {tasksOpen && (
          <div className="mt-3">
            <div className="mb-3 flex items-center justify-end gap-3">
              {reply.contact_id && (
                <TaskFormDialog
                  contactId={reply.contact_id}
                  trigger={
                    <Button type="button" variant="secondary" size="sm">
                      <Plus className="h-4 w-4" />
                      Add task
                    </Button>
                  }
                />
              )}
            </div>
            {reply.contact_id ? (
              <TaskList tasks={reply.tasks} emptyText="No tasks for this contact yet." />
            ) : (
              <p className="text-sm text-ink-muted">
                No contact linked to this thread yet — sync the thread or run the contact backfill to enable tasks here.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

function humanizeStage(value: string) {
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ")
}

const CRM_STAGE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: "warm_reply", label: "Warm Reply" },
  { value: "sample_sent", label: "Sample Sent" },
  { value: "booked", label: "Booked" },
  { value: "proposal", label: "Proposal" },
  { value: "closed_won", label: "Closed-Won" },
  { value: "onboarding", label: "Onboarding" },
  { value: "delivery", label: "Delivery" },
]

const CRM_STAGE_LABELS: Record<string, string> = Object.fromEntries(
  CRM_STAGE_OPTIONS.map((o) => [o.value, o.label]),
)

function CrmControl({ reply, clients }: { readonly reply: SelectedReply; readonly clients: readonly { readonly id: number; readonly display_name: string }[] }) {
  const [promoteOpen, setPromoteOpen] = React.useState(false)
  const [selectedClientId, setSelectedClientId] = React.useState<string>(reply.client ? String(reply.client.id) : "")

  const promoteToCrm = () => {
    router.post(`/replies/${reply.id}/promote`, { client_id: selectedClientId || undefined }, { preserveScroll: true })
    setPromoteOpen(false)
  }

  const moveCrmStage = (oppId: number, stage: string) => {
    router.post(`/crm/opportunities/${oppId}/stage`, { stage }, { preserveScroll: true })
  }

  if (reply.crm_opportunity) {
    const opp = reply.crm_opportunity
    const currentLabel = CRM_STAGE_LABELS[opp.coarse_stage] ?? opp.coarse_stage
    return (
      <div className="flex items-center gap-2">
        <Link href={`/crm/opportunities/${opp.id}`} className="text-[13px] no-underline hover:underline">
          View opportunity
        </Link>
        <Select
          aria-label="Change CRM stage"
          value=""
          onChange={(event) => event.target.value && moveCrmStage(opp.id, event.target.value)}
          className="h-8 w-auto min-w-36 text-xs"
        >
          <option value="" disabled>Move stage ({currentLabel})</option>
          {CRM_STAGE_OPTIONS.filter((o) => o.value !== opp.coarse_stage).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>
    )
  }

  if (!reply.client && clients.length === 0) {
    return (
      <div className="flex items-center gap-2">
        <Button type="button" variant="secondary" size="sm" disabled>
          Promote to CRM
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => {
          if (reply.client) {
            router.post(`/replies/${reply.id}/promote`, {}, { preserveScroll: true })
          } else {
            setSelectedClientId("")
            setPromoteOpen(true)
          }
        }}
      >
        Promote to CRM
      </Button>
      <Dialog open={promoteOpen} onOpenChange={(open) => !open && setPromoteOpen(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select client</DialogTitle>
          </DialogHeader>
          <label htmlFor="promote_client">Client</label>
          <Select
            id="promote_client"
            value={selectedClientId}
            onChange={(event) => setSelectedClientId(event.target.value)}
          >
            <option value="">Select a client</option>
            {clients.map((client) => (
              <option key={client.id} value={client.id}>{client.display_name}</option>
            ))}
          </Select>
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => setPromoteOpen(false)}>Cancel</Button>
            <Button type="button" disabled={!selectedClientId} onClick={promoteToCrm}>
              Promote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function NextActionBlock({
  replyId,
  note,
  nextFollowupOn,
}: {
  readonly replyId: number
  readonly note: string
  readonly nextFollowupOn: string | null
}) {
  const [value, setValue] = React.useState(note)
  const dirty = value.trim() !== note.trim()

  const save = () => {
    if (!dirty) return
    router.post(`/replies/${replyId}/next_step_note`, { note: value.trim() }, { preserveScroll: true })
  }

  return (
    <div className="mt-3">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-ink-muted">
        Next action
      </span>
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex min-w-[220px] flex-1 gap-2">
          <Input
            id="next_step_note"
            value={value}
            placeholder="What needs to happen?"
            onChange={(event) => setValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault()
                save()
              }
            }}
          />
          <Button type="button" variant="secondary" size="sm" disabled={!dirty} onClick={save}>
            Save
          </Button>
        </div>
        <FollowupDateControl
          replyId={replyId}
          nextFollowupOn={nextFollowupOn}
          id={`thread_followup_${replyId}`}
        />
      </div>
    </div>
  )
}
